import { useEffect, useState } from "react";
import { Show, useAuth } from "@clerk/react";
import { Redirect, useLocation } from "wouter";
import { Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Task #383 — Guest reviewer landing after Clerk sign-in. The Clerk
 * invitation redirects here; we POST /api/guest/accept to flip the row
 * from pending → active and then jump into the scoped contract surface.
 */
export default function GuestAccept() {
  return (
    <>
      <Show when="signed-out">
        {/*
          Preserve the full original querystring (notably ?contractGuestId=…)
          across the Clerk sign-in round-trip — otherwise the backend's
          /api/guest/accept returns 400 missing_contract_guest_id and the
          invitee is stranded after sign-in.
        */}
        <Redirect
          to={`/sign-in?redirect_url=${encodeURIComponent(
            `/guest/accept${typeof window !== "undefined" ? window.location.search : ""}`,
          )}`}
        />
      </Show>
      <Show when="signed-in">
        <GuestAcceptInner />
      </Show>
    </>
  );
}

function GuestAcceptInner() {
  const { isLoaded, getToken } = useAuth();
  const [, setLocation] = useLocation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        // The invitation link from Clerk redirects here with
        // ?contractGuestId=<id>. Forward it so the backend activates the
        // exact invited row (never an oldest-pending fallback).
        const params = new URLSearchParams(window.location.search);
        const cgid = params.get("contractGuestId");
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch("/api/guest/accept", {
          method: "POST",
          credentials: "include",
          headers,
          body: JSON.stringify({ contractGuestId: cgid ? Number(cgid) : undefined }),
        });
        if (cancelled) return;
        if (res.status === 400) {
          setError(
            "Open the invitation link directly from your email — it carries the unique invite identifier this page needs.",
          );
          return;
        }
        if (res.status === 404) {
          setError(
            "We couldn't find an active invitation for your email. Ask the admin who invited you to re-send the invite.",
          );
          return;
        }
        if (!res.ok) {
          setError("Something went wrong. Please try again or contact the admin who invited you.");
          return;
        }
        const data = (await res.json()) as { contractId: number };
        setLocation(`/guest/contracts/${data.contractId}`);
      } catch {
        if (!cancelled) setError("Network error. Please retry.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoaded, getToken, setLocation]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center space-y-4">
        {error ? (
          <>
            <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
            <h1 className="text-lg font-semibold">Invitation could not be claimed</h1>
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button onClick={() => setLocation("/")}>Return home</Button>
          </>
        ) : (
          <>
            <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
            <h1 className="text-lg font-semibold">Setting up your reviewer access…</h1>
            <p className="text-sm text-muted-foreground">
              We're matching your invitation. This only takes a second.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
